import Price from "@JohnLewisPartnership/jl-design-system/dist/components/price/Price";
import Rating from "@JohnLewisPartnership/jl-design-system/dist/components/rating/Rating";
import styles from "./PriceAndReviews.module.scss";
import { PRODUCT_REVIEWS_ACCORDION_INSTANCE_NAME } from "../ProductAccordion/components/ProductReviewsAccordion/ProductReviewsAccordionUI";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";

const dispatchOpenProductReviewsEvent = () => {
  document.body.dispatchEvent(
    new CustomEvent(
      `jlds:accordion:${PRODUCT_REVIEWS_ACCORDION_INSTANCE_NAME}`,
      {
        detail: { action: "open" },
      },
    ),
  );
};

type Props = {
  /**
   * A string representing the brand of the product. It can also be null or undefined.
   */
  brand: string | undefined | null;
  /**
   * An object representing the price of the product. It can also be undefined. The price object can have 'was', 'then1', 'then2', and 'now' properties, each of which can be a string or an object with 'from' and 'to' properties.
   */
  price:
    | {
        was?: string | { from: string; to: string };
        then1?: string | { from: string; to: string };
        then2?: string | { from: string; to: string };
        now: string | { from: string; to: string };
      }
    | undefined;
  /**
   * An object representing the rating of the product. It can also be null or undefined. The rating object has 'averageRating' and 'reviews' properties.
   */
  rating:
    | {
        averageRating: number;
        reviews: number;
      }
    | null
    | undefined;
  /**
   * Boolean to decide to display price reduction history as now was (default is was now)
   */
  isNowWas: boolean;
};

export const PriceAndReviewsUI = ({
  brand,
  price,
  rating,
  isNowWas = false,
}: Props) => {
  const handleReviewClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    dispatchOpenProductReviewsEvent();

    const scrollIntoView = () =>
      document
        .querySelector("#ratings-reviews-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });

    setTimeout(scrollIntoView, 0);
  };

  if (!price && !rating) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="PriceAndReviews">
      {price && (
        <Price
          brand={brand}
          className={styles.price}
          price={price}
          usageContext="pdp"
          showPriceReduction={false}
          isNowWas={isNowWas}
        />
      )}
      {rating && rating.reviews > 0 && (
        <a
          className={styles.ratings}
          title={`${rating.averageRating} out of 5 stars`}
          aria-label={`${rating.averageRating} out of 5 stars`}
          onClick={handleReviewClick}
          href="#ratings-reviews-section"
        >
          <Rating size="large" suffixType="none" rating={rating} />
          <UnderlinedButton>
            {`${rating.reviews} Review${rating.reviews > 1 ? "s" : ""}`}
          </UnderlinedButton>
        </a>
      )}
    </div>
  );
};
